'use client';

import { useEffect, type Dispatch, type SetStateAction } from 'react';

import { parseAuthenticatedUser } from '@/lib/auth/parse-authenticated-user';
import type { AuthenticatedUser } from '@/lib/auth/types';
import { ORGANIZATION_FEATURE_LABELS, type OrganizationFeature } from '@/lib/organization-features';

type PolicyScope = Pick<AuthenticatedUser, 'id' | 'currentOrganizationId' | 'currentWorkspaceId'>;

function hasSameScope(user: AuthenticatedUser | null, scope: PolicyScope): user is AuthenticatedUser {
  return user?.id === scope.id && user.currentOrganizationId === scope.currentOrganizationId
    && user.currentWorkspaceId === scope.currentWorkspaceId;
}

function updatePolicy(current: AuthenticatedUser | null, next: AuthenticatedUser): AuthenticatedUser | null {
  if (!hasSameScope(current, next)) return current;
  if (current.organization?.id !== next.organization?.id) return next;
  const changed = (Object.keys(ORGANIZATION_FEATURE_LABELS) as OrganizationFeature[]).some(
    feature => current.organization?.settings?.[feature] !== next.organization?.settings?.[feature],
  );
  return changed ? next : current;
}

async function fetchPolicy(signal: AbortSignal): Promise<AuthenticatedUser | null> {
  const response = await fetch('/api/auth/me', {
    cache: 'no-store',
    signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
  });
  if (!response.ok) return null;
  const payload = await response.json() as { user?: unknown };
  return parseAuthenticatedUser(payload.user).user;
}

async function refreshPolicyForScope(
  scope: PolicyScope,
  organization: AuthenticatedUser['organization'] | undefined,
  signal: AbortSignal,
  setUser: Dispatch<SetStateAction<AuthenticatedUser | null>>,
): Promise<void> {
  const next = await fetchPolicy(signal);
  if (signal.aborted || !hasSameScope(next, scope)) return;
  // 保存・auth refresh 後に届いた古い応答で、適用済みの組織設定を戻さない。
  setUser(current => current?.organization === organization ? updatePolicy(current, next) : current);
}

/** 他クライアントの OFF を表示中の本文にも反映する。本文の polling は OFF で停止する。 */
export function useOrganizationPolicyRefresh(
  user: AuthenticatedUser | null,
  setUser: Dispatch<SetStateAction<AuthenticatedUser | null>>,
): void {
  const id = user?.id;
  const currentOrganizationId = user?.currentOrganizationId;
  const currentWorkspaceId = user?.currentWorkspaceId;
  const organization = user?.organization;
  useEffect(() => {
    if (!id || !currentOrganizationId) return;
    const scope = { id, currentOrganizationId, currentWorkspaceId };
    const controller = new AbortController();
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState !== 'visible') return;
      pending = true;
      try {
        await refreshPolicyForScope(scope, organization, controller.signal, setUser);
      } catch {
        // API は最新 DB 設定で強制する。通信復帰後に表示ポリシーを再取得する。
      } finally {
        pending = false;
      }
    };
    const onFocus = () => { void refresh(); };
    const timer = window.setInterval(onFocus, 30_000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [id, currentOrganizationId, currentWorkspaceId, organization, setUser]);
}
